import { Chip } from '@mui/material';
import { CallMade, CallReceived } from '@mui/icons-material';
import { formatSecondsToTime } from '@/utils/formatting';
import QualityScoreChip from './QualityScoreChip';

import type { ColumnDef } from '@tanstack/react-table';
import type { Call } from '../../../types/calls';
import { mapCallEndingReasonToLabel } from '../CallDetails/callDetailsUtils';

export const getDirectionIcon = (direction: string): React.ReactNode => {
    return direction === 'in' ? <CallReceived sx={{ fontSize: '0.75rem' }} /> : <CallMade sx={{ fontSize: '0.75rem' }} />;
};

export const getDirectionLabel = (direction: string): string => {
    return direction === 'in' ? 'Inbound' : 'Outbound';
};

interface GetCallListColumnsOptions {
    hasQualityScoreFormula: boolean;
    formatDate: (date: Date | string | null) => string;
}

const getCallListColumns = ({ hasQualityScoreFormula, formatDate }: GetCallListColumnsOptions): ColumnDef<Call>[] => {
    const columns: ColumnDef<Call>[] = [
        {
            accessorKey: 'created_at',
            header: 'Date and time',
            enableSorting: true,
            cell: ({ row }) => formatDate(row.original.created_at),
        },
        {
            accessorKey: 'number',
            header: 'Number',
            cell: ({ getValue }) => getValue() || 'N/A',
            enableSorting: true,
        },
        {
            accessorKey: 'agent_name',
            header: 'Agent',
            enableSorting: true,
        },
        {
            accessorKey: 'talk_time',
            header: 'Call duration',
            cell: ({ getValue }) => {
                const talkTime = getValue() as number | null;
                return talkTime !== null ? formatSecondsToTime(talkTime) : 'N/A';
            },
            enableSorting: true,
            size: 100,
        },
        {
            accessorKey: 'reason_name',
            header: 'Outcome',
            enableSorting: true,
            size: 100,
            cell: ({ row }) => mapCallEndingReasonToLabel(row.original.reason_name || null),
        },
    ];

    if (hasQualityScoreFormula) {
        columns.push({
            accessorKey: 'quality_score',
            header: 'Score',
            enableSorting: true,
            size: 100,
            cell: ({ getValue }) => {
                const score = getValue() as number | null | undefined;

                return <QualityScoreChip score={score} />;
            },
        });
    }

    columns.push({
        accessorKey: 'direction',
        header: 'Direction',
        cell: ({ getValue }) => {
            const direction = getValue() as string;
            if (!direction) return 'N/A';

            return (
                <Chip
                    label={getDirectionLabel(direction)}
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={getDirectionIcon(direction) as React.ReactElement}
                />
            );
        },
        enableSorting: true,
    });

    return columns;
};

export default getCallListColumns;
